<template>
<div class="privacy">
    <Head>
        <title>{{ pageName }}</title>
        <meta name="description" :content="metaDescription" />
        <meta name="keywords" :content="metaKeywords" />
    </Head>
    <h1 class="headline-xl privacy-title cms-feature">{{ pageName }}</h1>
    <Cms
        class="privacy-builder-content"
        content-prop="cmsContent"
        model="content-section"
        :search-params="searchParams"
        driver="builder"
        v-model="builderData" 
        :readable-fields="['pageName', 'metaDescription', 'metaKeywords']"
        :components="getBuilderComponents()"
        is-section
    />
    <form class="two-column-form privacy-form" @submit.prevent="submitForm()">
        <div class="input-row">
            <!-- Request Type -->
            <div class="field-state input-folder">
                <custom-select 
                    :options="formattedRequestTypes" 
                    v-model="requestTypeValue" 
                    label="Request Type *"
                    aria-label="Select a request type"
                    :error="form.errors.request_type != null"
                />
                <div v-if="form.errors.request_type" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.request_type }}
                    </p>
                </div>
            </div>
            <!-- Email Address -->
            <div class="field-state input-folder">
                <div class="input-folder--input">
                    <PrimeInputText 
                        v-model="form.email"
                        type="text" 
                        id="email" 
                        placeholder=" " 
                        aria-label="Enter your email"
                        :class="{ 'invalid-state-input': form.errors.email != null }"
                        name="email"
                    />
                    <label for="email">
                        <p class="paragraph-default">Email *</p>
                    </label>
                </div>
                <div v-if="form.errors.email" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.email }}
                    </p>
                </div>
            </div>
        </div>
        <div class="input-row">
            <!-- First Name -->
            <div class="field-state input-folder">
                <div class="input-folder--input">
                    <PrimeInputText 
                        v-model="form.first_name"
                        type="text" 
                        id="first_name" 
                        placeholder=" " 
                        aria-label="Enter your first name"
                        :class="{ 'invalid-state-input': form.errors.first_name != null }"
                        name="first_name"
                    />
                    <label for="first_name">
                        <p class="paragraph-default">First Name *</p>
                    </label>
                </div>
                <div v-if="form.errors.first_name" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.first_name }}
                    </p>
                </div>
            </div>
            <!-- Last Name -->
            <div class="field-state input-folder">
                <div class="input-folder--input">
                    <PrimeInputText 
                        v-model="form.last_name"
                        type="text" 
                        id="last_name" 
                        placeholder=" " 
                        aria-label="Enter your last name"
                        :class="{ 'invalid-state-input': form.errors.last_name != null }"
                        name="last_name"
                    />
                    <label for="last_name">
                        <p class="paragraph-default">Last Name *</p>
                    </label>
                </div>
                <div v-if="form.errors.last_name" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.last_name }}
                    </p>
                </div>
            </div>
        </div>
        <div class="input-row">
            <!-- Country -->
            <div class="field-state input-folder">
                <custom-select 
                    :options="formattedCountries" 
                    v-model="countryValue" 
                    label="Country *"
                    aria-label="Select a country"
                    :error="form.errors.country != null"
                />
                <div v-if="form.errors.country" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.country }}
                    </p>
                </div>
            </div>
            <!-- State / Region -->
            <div class="field-state input-folder">
                <custom-select
                    v-if="formattedRegions.length > 0" 
                    :options="formattedRegions" 
                    v-model="regionValue" 
                    :label="`${countryValue.key == 'USA' ? 'State' : 'Region'} ${regionRequired ? '*' : ''}`"
                    aria-label="Select a state/region"
                    :error="form.errors.region != null"
                />
                <div v-else class="input-folder--input">
                    <PrimeInputText 
                        v-model="form.region"
                        type="text" 
                        id="region" 
                        placeholder=" " 
                        aria-label="Enter enter a region"
                        name="region"
                        :class="{ 'invalid-state-input': form.errors.region != null }"
                    />
                    <label for="region">
                        <p class="paragraph-default">{{ `${countryValue.key == 'USA' ? 'State' : 'Region'} ${regionRequired ? '*' : ''}` }}</p>
                    </label>
                </div>
                <div v-if="form.errors.region" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.region }}
                    </p>
                </div>
            </div>
        </div>
        <!-- Additional Info -->
        <div class="field-state">
            <div class="textarea-folder">
                <PrimeTextArea 
                    v-model="form.additional_info"
                    id="additional_info"
                    name="additional_info"
                    placeholder=" " class="input-folder--input">
                </PrimeTextArea>
                <label class="paragraph-default" for="additional_info">Additional Info (Optional)</label>
            </div>
        </div>
        <div v-if="!isLoggedIn" class="privacy-footer-row">
            <!-- Recaptcha -->
            <div class="flex-column">
                <Recaptcha v-model="form.recaptchaResponse" theme="light"/>
                <div v-if="form.errors.recaptchaResponse" class="field-state input-folder">
                    <p class="input-support-text invalid-state">
                        {{ form.errors.recaptchaResponse }}
                    </p>
                </div>
            </div>
        </div>
        <div v-if="!requestSuccessful" class="privacy-footer-row">
            <RothcoCta :disabled="form.processing" class="primary" id="submit_form" type="submit">Submit</RothcoCta>
        </div>
        <div v-else class="field-state">
            <div class="notification notification-success">
                <p class="paragraph-details"><strong>Request Sent!</strong></p>
            </div>
        </div>
    </form>
</div>
</template>
<script setup lang="ts">
    import Cms from "@/components/Cms.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import Recaptcha from "@/components/Recaptcha.vue";
    import { storeToRefs } from "pinia";
    import { ref, computed, watch, inject, type PropType } from "vue";
    import { getBuilderComponents } from "@root/builder-components.ts";
    import {useSettingsStore } from '@/store/settings';
    import { usePage, Head } from '@inertiajs/vue3';
    import { useForm, getFirstInvalidInputElement } from "@/helpers/composables/useForm";
    import { string, object } from "yup";

    interface Region {
        code: string;
        name: string;
    }

    interface Country {
        alpha3code: string;
        alpha2code: string;
        name: string;
    }

    let route = inject('route');

    const props = defineProps({
        regions: {
            type: Array as PropType<Region[]>,
            default: () => []
        },
        countries: {
            type: Array as PropType<Country[]>,
            default: () => []
        },
        cmsContent: {
            type: Object,
            default: () => ({
                data: {
                    pageName: null,
                    metaDescription: "",
                    metaKeywords: ""
                }
            })
        }
    });

    const requestSuccessful = ref(false);

    const getRegisteredComponents = () => registeredComponents;
    let searchParams = computed(()=>{
        return {
            'data.pageUrl.$eq': 'do-not-sell-my-info'
        }
    });

    let builderData = ref({
        pageName: ""
    });
    let pageName = computed(()=>{
        return props.cmsContent?.data?.pageName ?? builderData.value.pageName;
    })

    let metaDescription = computed(()=>{
        return props.cmsContent?.data?.metaDescription ?? builderData.value.metaDescription;
    })
    let metaKeywords = computed(()=>{
        return props.cmsContent?.data?.metaKeywords ?? builderData.value.metaKeywords;
    })

    let { setNotificationMessage } = useSettingsStore();
    const { isLoggedIn } = storeToRefs(useSettingsStore());

    const regionRequired = computed(()=>{
        return ['USA','CAN'].includes(countryValue.value.key);
    })

    const form = useForm({
        useInertia: true,
        schema: object({
            request_type: string().required("Please select a request type").default(''),
            first_name: string().required("Please enter a first name").default(''),
            last_name: string().required("Please enter a last name").default(''),
            email: string().email("Please enter a valid email").required("Please enter an email").default(usePage().props?.user?.email ?? ''),
            country: string().required("Please select a country").default(''),
            region: string().default('').test(
                'is-not-empty',
                'State/Region is required',
                (value) => {
                    if(form.country == 'USA' || form.country == 'CAN'){
                        return  value !== undefined && value !== '' && value !== null;
                    }
                    return true;
                }
            ),
            additional_info: string().notRequired().default(''),
            recaptchaResponse: string().test(
                'recaptchaResponse',
                'Please complete the captcha',
                value => isLoggedIn.value ? true : !!value
            )
        })
    });

    const submitForm  = async () => {
        if(form.processing) return;

        await form.validate();
        if(!form.hasErrors){
            form.transform((data)=>({
                ...data,
                dealer_no: usePage()?.props?.user?.dealer_no,
                'g-recaptcha-response': data.recaptchaResponse
            })).post(
                route("account.doNotSellMyInfo.submit"),{
                    preserveScroll: true,
                    onBefore: () => {
                        requestSuccessful.value = false;
                    },
                    onSuccess: (ctx) => {
                        setNotificationMessage({
                            message: 'Request sent!',
                            type: 'success'
                        })
                        requestSuccessful.value = true; 
                    },
                    onError: (err)=>{
                        if(err.message){
                            setNotificationMessage({
                                message: err.message,
                                type: 'error'
                            })
                        }
                        requestSuccessful.value = false;
                    }
                }
            )
        }else{
            let invalidInputElement = getFirstInvalidInputElement();
            if(invalidInputElement){
                invalidInputElement.scrollToAndFocus();
            }
        }
    };

    const requestTypes = [
        'Right to Opt-Out of Targeted Advertising',
        'Do Not Sell or Share My Information',
        'Send Me a Copy of My Information',
        'Right to Delete My Data',
        'Right to Correct My Data'
    ];
    const formattedRequestTypes = computed(()=>{
        return requestTypes.map((type)=>({
            key: type, value: type
        }))
    })
    const requestTypeValue = computed({
        get(){
            return formattedRequestTypes.value.find((type)=>(type.key == form.request_type)) ?? {key: '', value: ''}
        },
        set(newVal){
            form.request_type = newVal.key;
        }
    })

    const formattedCountries = computed(()=>{
        return props.countries.map((country)=>({
            key: country.alpha3code, value: country.name
        }))
    })
    const countryValue = computed({
        get(){
            return formattedCountries.value.find((country)=>(country.key == form.country)) ?? {key: '', value: ''}
        },
        set(newVal){
            form.country = newVal.key;
        }
    })

    const regionValue = computed({
        get(){
            return formattedRegions.value.find((region)=>(region.key == form.region)) ?? {key: '', value: ''}
        },
        set(newVal){
            form.region = newVal.key;
        }
    })

    const CANADA_STATE_CODES = new Set(['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']);
    let formattedRegions = computed(()=>{
        const regions = props.regions;
        const countryRegionMap = {
            'CAN': regions
                .filter(region => CANADA_STATE_CODES.has(region.code) && region.code !== 'PR')
                .map(region => ({ key: region.code, value: region.name })),
            'PRI': [{ key: 'PR', value: 'Puerto Rico' }],
            'USA': regions
                .filter(region => !CANADA_STATE_CODES.has(region.code))
                .map(region => ({ key: region.code, value: region.name }))
        };
        return countryRegionMap[form.country] ?? [];
    })

    watch(formattedRegions, (newVal) => {
        if(newVal){
            let state = form.region?.toLowerCase();
            for(let availableState of newVal){
                if(availableState.value?.toLowerCase() == state || availableState.key?.toLowerCase() == state){
                    form.region = availableState.key;
                    break;
                }
            }
        }
    });

</script> 